import React, { useState } from 'react';
import { User, Palette, Type, CreditCard, ShoppingBag, Settings as SettingsIcon, Camera, Upload } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useFont, fonts } from '../context/FontContext';
import { useAuth } from '../context/AuthContext';
import { branding } from '../config/branding';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';

const Settings = () => {
  const { currentTheme, changeTheme, themes } = useTheme();
  const { currentFont, changeFont } = useFont();
  const { user, updateUser, updateBilling } = useAuth();

  const [accountForm, setAccountForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    profilePic: user?.profilePic || branding.defaultImages.profilePlaceholder
  });

  const [billingForm, setBillingForm] = useState({
    cardNumber: '',
    expiryMonth: user?.billing?.expiryMonth || '',
    expiryYear: user?.billing?.expiryYear || '',
    cvv: ''
  });

  const [imagePreview, setImagePreview] = useState(accountForm.profilePic);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In real app, upload to server. For demo, use object URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setAccountForm({ ...accountForm, profilePic: reader.result });
      };
      reader.readAsDataURL(file);
      toast({
        title: "Image Selected",
        description: "Click Save Changes to update your profile picture.",
      });
    }
  };

  const handleAccountUpdate = (e) => {
    e.preventDefault();
    updateUser(accountForm);
    toast({
      title: "Account Updated",
      description: "Your account details have been saved successfully.",
    });
  };

  const handleBillingUpdate = (e) => {
    e.preventDefault();
    const last4 = billingForm.cardNumber.slice(-4);
    updateBilling({
      cardLast4: last4,
      cardBrand: 'Visa',
      expiryMonth: billingForm.expiryMonth,
      expiryYear: billingForm.expiryYear
    });
    toast({
      title: "Payment Method Updated",
      description: "Your billing information has been saved securely.",
    });
    setBillingForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                 style={{ backgroundColor: `${currentTheme.primary}20` }}>
              <SettingsIcon className="w-6 h-6" style={{ color: currentTheme.primary }} />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white uppercase">Settings</h1>
          </div>
          <p className="text-xl text-gray-400">Manage your account, preferences, and purchases</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="account" className="space-y-8">
          <TabsList className="bg-zinc-900 border border-white/10 p-1">
            <TabsTrigger value="account" className="data-[state=active]:bg-white/10">
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
            <TabsTrigger value="appearance" className="data-[state=active]:bg-white/10">
              <Palette className="w-4 h-4 mr-2" />
              Appearance
            </TabsTrigger>
            <TabsTrigger value="purchases" className="data-[state=active]:bg-white/10">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Purchases
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 uppercase">Account Information</h2>
              
              {/* Profile Picture Upload */}
              <div className="mb-8">
                <label className="text-gray-400 text-sm mb-4 block">Profile Picture</label>
                <div className="flex items-center gap-6">
                  <div className="relative group">
                    <img 
                      src={imagePreview} 
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-2 border-white/10 group-hover:border-white/30 transition-all"
                    />
                    <label 
                      htmlFor="profile-upload"
                      className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <Camera className="w-6 h-6 text-white" />
                    </label>
                    <input 
                      id="profile-upload"
                      type="file" 
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  <div>
                    <label 
                      htmlFor="profile-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 text-white text-sm font-medium cursor-pointer transition-all active:scale-95"
                    >
                      <Upload className="w-4 h-4" />
                      Upload New Photo
                    </label>
                    <p className="text-gray-500 text-xs mt-2">
                      JPG, PNG or GIF. Max size 5MB.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAccountUpdate} className="space-y-6">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Full Name</label>
                  <input
                    type="text"
                    value={accountForm.name}
                    onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Email Address</label>
                  <input
                    type="email"
                    value={accountForm.email}
                    onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Member Since</label>
                  <div className="px-4 py-3 rounded-lg bg-black/30 border border-white/5 text-gray-400">
                    {new Date(user?.joinDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-black font-bold hover:scale-105 transition-transform active:scale-95"
                  style={{ backgroundColor: currentTheme.primary }}
                >
                  Save Changes
                </Button>
              </form>
            </div>

            {/* Billing Information */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 uppercase">Billing Details</h2>
              
              {user?.billing && (
                <div className="mb-6 p-4 rounded-lg bg-black/30 border border-white/5">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-gray-400" />
                    <div>
                      <div className="text-white font-medium">
                        {user.billing.cardBrand} •••• {user.billing.cardLast4}
                      </div>
                      <div className="text-gray-400 text-sm">
                        Expires {user.billing.expiryMonth}/{user.billing.expiryYear}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleBillingUpdate} className="space-y-6">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Card Number</label>
                  <input
                    type="text"
                    value={billingForm.cardNumber}
                    onChange={(e) => setBillingForm({ ...billingForm, cardNumber: e.target.value.replace(/\s/g, '') })}
                    placeholder="1234 5678 9012 3456"
                    maxLength="16"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': currentTheme.primary }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Month</label>
                    <input
                      type="text"
                      value={billingForm.expiryMonth}
                      onChange={(e) => setBillingForm({ ...billingForm, expiryMonth: e.target.value })}
                      placeholder="MM"
                      maxLength="2"
                      className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': currentTheme.primary }}
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Year</label>
                    <input
                      type="text"
                      value={billingForm.expiryYear}
                      onChange={(e) => setBillingForm({ ...billingForm, expiryYear: e.target.value })}
                      placeholder="YYYY"
                      maxLength="4"
                      className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': currentTheme.primary }}
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">CVV</label>
                    <input
                      type="text"
                      value={billingForm.cvv}
                      onChange={(e) => setBillingForm({ ...billingForm, cvv: e.target.value })}
                      placeholder="123"
                      maxLength="4"
                      className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': currentTheme.primary }}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-black font-bold hover:scale-105 transition-transform active:scale-95"
                  style={{ backgroundColor: currentTheme.primary }}
                >
                  Update Payment Method
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="space-y-6">
            {/* Theme Selection */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Palette className="w-6 h-6" style={{ color: currentTheme.primary }} />
                <h2 className="text-2xl font-bold text-white uppercase">Color Theme</h2>
              </div>
              <p className="text-gray-400 mb-6">Choose a color theme that matches your style</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => changeTheme(theme.id)}
                    className={`p-4 rounded-xl border-2 transition-all hover:scale-105 active:scale-95 ${
                      currentTheme.id === theme.id 
                        ? 'border-white/30 bg-white/5' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div 
                      className="w-full h-16 rounded-lg mb-3"
                      style={{ backgroundColor: theme.primary }}
                    />
                    <div className="text-white text-sm font-medium">{theme.name}</div>
                    {currentTheme.id === theme.id && (
                      <div className="text-xs mt-1 font-bold" style={{ color: theme.primary }}>
                        Active
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Font Selection */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Type className="w-6 h-6" style={{ color: currentTheme.primary }} />
                <h2 className="text-2xl font-bold text-white uppercase">Font Style</h2>
              </div>
              <p className="text-gray-400 mb-6">Select your preferred typography</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fonts.map((font) => (
                  <button
                    key={font.id}
                    onClick={() => changeFont(font.id)}
                    className={`p-6 rounded-xl border-2 transition-all hover:scale-105 active:scale-95 text-left ${
                      currentFont.id === font.id 
                        ? 'border-white/30 bg-white/5' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-white font-medium">{font.name}</div>
                      {currentFont.id === font.id && (
                        <div className="text-xs px-2 py-1 rounded font-bold text-black" style={{ backgroundColor: currentTheme.primary }}>
                          Active
                        </div>
                      )}
                    </div>
                    <div 
                      className="text-2xl text-gray-300"
                      style={{ fontFamily: font.fontFamily }}
                    >
                      The Quick Brown Fox
                    </div>
                    <div className="text-xs text-gray-500 mt-2">{font.category}</div>
                  </button>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases" className="space-y-6">
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 uppercase">Purchase History</h2>
              
              {user?.purchases && user.purchases.length > 0 ? (
                <div className="space-y-4">
                  {user.purchases.map((purchase) => (
                    <div 
                      key={purchase.id}
                      className="p-6 rounded-xl bg-black/30 border border-white/5 hover:border-white/10 transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                        <div>
                          <div className="text-white font-bold mb-1">Order #{purchase.id}</div>
                          <div className="text-gray-400 text-sm">
                            {new Date(purchase.date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        <div className="mt-2 md:mt-0">
                          <div className="text-2xl font-bold text-white">${purchase.total.toFixed(2)}</div>
                          <div className="text-xs px-2 py-1 rounded text-center mt-1" 
                               style={{ backgroundColor: `${currentTheme.primary}30`, color: currentTheme.primary }}>
                            {purchase.status}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 border-t border-white/5 pt-4">
                        {purchase.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-400">
                              {item.title || item.name}
                              {item.size && ` (${item.size})`}
                              {item.type === 'beat' && ' - Beat'}
                              {item.type === 'merch' && ' - Merch'}
                            </span>
                            <span className="text-white font-medium">${item.price.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-4 border-white/10 hover:border-white/30 active:scale-95"
                      >
                        Download Receipt
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No purchases yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;